import { useEffect, useMemo, useState } from 'react';
import { style } from 'typestyle';
import Tooltip from '@mui/material/Tooltip';
import { ActionButton } from '@src/components/ActionButton';
import { Select, SelectOption } from '@src/components/Select';
import { SectionTitle } from '@src/components/SectionTitle';
import { InfoSvgIcon } from '@src/components/SvgIcons/Info';
import { ADALONG_COLORS } from '@src/styles';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import { LanguageSearch } from '@src/fragments/Filters/managers/LanguageSearch';
import {
  createSubtitleTranslation,
  getSubtitleTracks,
  getSubtitleTranslationJob,
  getSubtitleTranslationJobs,
  getTranslationLanguages,
  SubtitleTrack,
  SubtitleTranslationJob,
  TranslationLanguage,
} from '@src/queries/media/api';
import { infoSectionClass } from './styles';
import { queryClient, QUERY_KEYS } from '@src/query';

interface Props {
  mediaId: string;
  selectedLanguage: string;
  onLanguageSelect: (language: string) => void;
  transcription?: string;
}

const NON_TERMINAL_STATES = new Set(['queued', 'processing', 'uploaded', 'indexing']);

function normalizeLanguage(language: string): string {
  return language.trim().toLowerCase();
}

function statusText(job: SubtitleTranslationJob | undefined): string | undefined {
  if (!job) return undefined;
  if (job.status === 'done') return undefined;
  if (job.status === 'error') return job.errorCode ? `${lang.error}: ${job.errorCode}` : lang.error;
  return lang.statuses[job.status];
}

export function SubtitleTranslationPanel({
  mediaId,
  selectedLanguage,
  onLanguageSelect,
  transcription,
}: Props) {
  const [tracks, setTracks] = useState<SubtitleTrack[]>([]);
  const [languages, setLanguages] = useState<TranslationLanguage[]>([]);
  const [jobs, setJobs] = useState<SubtitleTranslationJob[]>([]);
  const [targetLanguage, setTargetLanguage] = useState<TranslationLanguage | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const jobsByLanguage = useMemo(() => new Map(
    jobs.map((job) => [normalizeLanguage(job.targetLanguage), job]),
  ), [jobs]);
  const availableLanguages = useMemo(() => {
    const existing = new Set(tracks.map((track) => normalizeLanguage(track.language)));
    return languages.filter((language) => {
      const job = jobsByLanguage.get(normalizeLanguage(language.iso6393));
      return !existing.has(normalizeLanguage(language.iso6393)) || (job !== undefined && job.status !== 'done');
    });
  }, [jobsByLanguage, languages, tracks]);
  const targetJob = targetLanguage ? jobsByLanguage.get(normalizeLanguage(targetLanguage.iso6393)) : undefined;
  const targetTrackExists = targetLanguage
    ? tracks.some((track) => normalizeLanguage(track.language) === normalizeLanguage(targetLanguage.iso6393))
    : false;
  const targetIsDone = targetJob?.status === 'done' || (!targetJob && targetTrackExists);
  const hasActiveJob = targetJob !== undefined && NON_TERMINAL_STATES.has(targetJob.status);
  const targetStatusText = statusText(targetJob);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    setTracks([]);
    setJobs([]);
    setLanguages([]);
    setTargetLanguage(null);
    setSubmitError(false);

    Promise.all([getSubtitleTracks(mediaId), getTranslationLanguages(), getSubtitleTranslationJobs(mediaId)])
      .then(([loadedTracks, loadedLanguages, loadedJobs]) => {
        if (cancelled) return;
        setTracks(loadedTracks);
        setLanguages(loadedLanguages);
        setJobs(loadedJobs);
        const activeJob = loadedJobs.find((job) => NON_TERMINAL_STATES.has(job.status));
        const selectedJob = activeJob || loadedJobs.find((job) => job.targetLanguage === selectedLanguage);
        if (selectedJob) {
          const selectedOption = loadedLanguages.find((language) => normalizeLanguage(language.iso6393) === normalizeLanguage(selectedJob.targetLanguage));
          if (selectedOption) setTargetLanguage(selectedOption);
        }
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [mediaId, selectedLanguage]);

  useEffect(() => {
    if (!targetJob || !NON_TERMINAL_STATES.has(targetJob.status)) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      getSubtitleTranslationJob(mediaId, targetJob.jobId)
        .then((updatedJob) => {
          if (cancelled) return;
          setJobs((current) => current.map((job) => job.jobId === updatedJob.jobId ? updatedJob : job));
          if (updatedJob.status === 'done') {
            onLanguageSelect(normalizeLanguage(updatedJob.targetLanguage));
            void queryClient.invalidateQueries({
              queryKey: [...QUERY_KEYS.media(mediaId), 'subtitles'],
            });
            void getSubtitleTracks(mediaId).then((updatedTracks) => {
              if (!cancelled) setTracks(updatedTracks);
            });
          }
        })
        .catch(() => undefined);
    }, 2000);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [mediaId, targetJob, onLanguageSelect]);

  const submit = async () => {
    if (!targetLanguage || targetIsDone || hasActiveJob || submitting) return;
    setSubmitting(true);
    setSubmitError(false);
    try {
      const job = await createSubtitleTranslation(mediaId, normalizeLanguage(targetLanguage.iso6393));
      setJobs((current) => [...current.filter((item) => item.jobId !== job.jobId && item.targetLanguage !== job.targetLanguage), job]);
    } catch {
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const translationOptions: SelectOption<string>[] = availableLanguages.map((language) => ({
    value: language.iso6393,
    text: language.displayName,
  }));

  return (
    <section className={infoSectionClass} aria-labelledby="transcription-section-title">
      <SectionTitle size="small">
        <span id="transcription-section-title">{lang.title}</span>
        <Tooltip title={lang.tooltip} placement="top">
          <button type="button" className={infoIconClass} aria-label={lang.tooltip}>
            <InfoSvgIcon />
          </button>
        </Tooltip>
      </SectionTitle>
      {loading ? <span>{lang.loading}</span> : null}
      {loadError ? <span role="alert">{lang.loadError}</span> : null}
      {!loading && !loadError ? (
        <>
          <div className={trackListClass} aria-label={lang.availableTracks}>
            {tracks.length ? tracks.map((track) => (
              <button
                type="button"
                className={trackButtonClass}
                key={track.language}
                aria-pressed={normalizeLanguage(track.language) === normalizeLanguage(selectedLanguage)}
                onClick={() => onLanguageSelect(normalizeLanguage(track.language))}
              >
                {displayLanguage(track.language, languages)}
              </button>
            )) : <span>{lang.noTracks}</span>}
          </div>
          {tracks.length ? (
            <>
              {translationOptions.length ? (
                <div className={translationControlsClass}>
                  <Select
                    options={translationOptions}
                    value={targetLanguage?.iso6393 || ''}
                    variant="adalong"
                    ariaLabel={lang.targetLanguage}
                    onChange={(value) => setTargetLanguage(
                      availableLanguages.find((language) => language.iso6393 === value) || null,
                    )}
                    displayEmpty
                    renderValue={() => targetLanguage?.displayName || lang.targetLanguage}
                    className={translationSelectClass}
                  />
                  <ActionButton
                    size="small"
                    minWidth="initial"
                    loading={submitting}
                    disabled={!targetLanguage || targetIsDone || hasActiveJob}
                    onAction={() => { void submit(); }}
                  >
                    {targetJob?.status === 'error' ? lang.retry : targetIsDone ? lang.available : lang.translate}
                  </ActionButton>
                </div>
              ) : <span>{lang.noLanguages}</span>}
              {targetJob && targetStatusText ? (
                <span role={targetJob.status === 'error' ? 'alert' : undefined}>{targetStatusText}</span>
              ) : null}
              {submitError ? <span role="alert">{lang.submitError}</span> : null}
            </>
          ) : null}
        </>
      ) : null}
      {transcription ? (
        <div className={transcriptionClass}>
          {transcription.replace(/\s+/g, ' ').trim()}
        </div>
      ) : null}
    </section>
  );
}

function displayLanguage(code: string, languages: TranslationLanguage[]): string {
  return languages.find((language) => normalizeLanguage(language.iso6393) === normalizeLanguage(code))?.displayName
    || LanguageSearch.translateIso6393(code)
    || code.toUpperCase();
}

const trackListClass = style({ display: 'flex', flexWrap: 'wrap', gap: '6px' });
const trackButtonClass = style({
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 30,
  padding: '4px 10px',
  border: `1px solid ${ADALONG_COLORS.FLASH_BLUE}`,
  borderRadius: 999,
  color: ADALONG_COLORS.LIGHT_GRAY,
  background: ADALONG_COLORS.LIGHT_BLACK,
  cursor: 'pointer',
  font: 'inherit',
  fontSize: 12,
  transition: 'background-color .2s, border-color .2s, color .2s',
  $nest: {
    '&[aria-pressed="true"]': {
      background: ADALONG_COLORS.FLASH_BLUE,
      borderColor: ADALONG_COLORS.FLASH_BLUE,
      color: ADALONG_COLORS.LIGHT_WHITE,
      fontWeight: 700,
    },
    '&:hover:not(:disabled):not([aria-pressed="true"])': {
      background: ADALONG_COLORS.DARK_GRAY,
      color: ADALONG_COLORS.LIGHT_WHITE,
    },
    '&[aria-pressed="true"]:hover:not(:disabled)': {
      background: ADALONG_COLORS.FLASH_BLUE,
      borderColor: ADALONG_COLORS.FLASH_BLUE,
      color: ADALONG_COLORS.LIGHT_WHITE,
    },
    '&:focus-visible': {
      outline: `2px solid ${ADALONG_COLORS.FLASH_BLUE}`,
      outlineOffset: 2,
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.55,
    },
  },
});
const translationControlsClass = style({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '8px',
  alignSelf: 'flex-start',
});
const translationSelectClass = style({
  minWidth: 160,
  border: `1px solid ${ADALONG_COLORS.FLASH_BLUE} !important`,
  borderRadius: '999px !important',
  transition: 'background-color .2s, border-color .2s, color .2s',
  $nest: {
    '&:hover': {
      borderColor: `${ADALONG_COLORS.LIGHT_WHITE} !important`,
    },
    '&.Mui-focused': {
      borderColor: `${ADALONG_COLORS.FLASH_BLUE} !important`,
      boxShadow: `0 0 0 2px ${ADALONG_COLORS.FLASH_BLUE}55`,
    },
    '&.Mui-disabled': {
      opacity: 0.55,
    },
  },
});
const infoIconClass = style({
  display: 'inline-flex',
  verticalAlign: 'middle',
  marginLeft: 8,
  padding: 0,
  border: 0,
  color: ADALONG_COLORS.LIGHT_GRAY,
  background: 'transparent',
  cursor: 'help',
  font: 'inherit',
  $nest: {
    '&:focus-visible': {
      outline: `2px solid ${ADALONG_COLORS.FLASH_BLUE}`,
      outlineOffset: 2,
    },
  },
});
const transcriptionClass = style({
  whiteSpace: 'normal',
});

const lang = LocalizedStrings({
  en: {
    title: 'Transcription',
    tooltip: 'Transcriptions and translations are also available as subtitles in the widget video player.',
    availableTracks: 'Available subtitle tracks',
    targetLanguage: 'Translate to',
    translate: 'Translate',
    retry: 'Retry translation',
    available: 'Already available',
    loading: 'Loading subtitle tracks…',
    loadError: 'Subtitle information could not be loaded.',
    noTracks: 'No public subtitle track is available.',
    noLanguages: 'No supported language found.',
    error: 'Translation failed',
    submitError: 'Translation could not be started. Please try again.',
    statuses: {
      queued: 'Translation queued',
      processing: 'Translation in progress',
      uploaded: 'Translation uploaded; indexing pending',
      indexing: 'Indexing translation',
      error: 'Translation failed',
    },
  },
});
